import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { CONSTANTS } from 'src/app/constants';
import { AnalyzersService } from 'src/app/providers/services/analyzers.service';
import { FlowmetersService } from 'src/app/providers/services/flowmeters.service';
import { GatewaysService } from 'src/app/providers/services/gateways.service';
import { LoaderService } from 'src/app/providers/services/loader.service';
import { MaterialsService } from 'src/app/providers/services/materials.service';
import { MissionsService } from 'src/app/providers/services/missions.service';
import { NavigationService } from 'src/app/providers/services/navigation.service';
import { PumpsService } from 'src/app/providers/services/pumps.service';
import { SkidsService } from 'src/app/providers/services/skids.service';
import { AnalyzerMission } from 'src/app/storage/models/analyzer-mission.model';
import { Mission } from 'src/app/storage/models/mission.model';
import { SkidMission } from 'src/app/storage/models/skid-mission.model';
import { AnalyzerSelection } from '../../common/add-analyzer/add-analyzer.component';
import { SkidSelection } from '../../common/add-skid/add-skid.component';
import { ITrain } from 'src/app/storage/interfaces/train.interface';
import { IInjection } from 'src/app/storage/interfaces/injection.interface';
import { TrainsService } from 'src/app/providers/services/trains.service';
import { InjectionsService } from 'src/app/providers/services/injections.service';

@Component({
    selector: 'app-material-config',
    templateUrl: './material-config.component.html',
    styleUrls: ['./material-config.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MaterialConfigComponent implements OnInit, OnDestroy {

  mission: Mission;

  missionSkids: SkidMission[] = [];
  missionTrains: ITrain[] = [];
  missionInjections: IInjection[] = [];
  addingSkid = false;
  missionSkidToUpdate: SkidMission;

  missionAnalyzers: AnalyzerMission[] = [];
  addingAnalyzer = false;

  validatingMaterial = false;

  private missionSubscription: Subscription;
  private skidsSubscription: Subscription;
  private analyzerSubscription: Subscription;

  constructor(
    private missionsService: MissionsService,
    private skidsService: SkidsService,
    private analyzersServices: AnalyzersService,
    private navigationService: NavigationService,
    private pumpsService: PumpsService,
    private gatewaysService: GatewaysService,
    private flowmetersService: FlowmetersService,
    private loader: LoaderService,
    private materialsService: MaterialsService,
    private trainsService: TrainsService,
    private injectionsService: InjectionsService
  ) { }

  ngOnInit(): void {
    this.missionSubscription = this.missionsService.selectedMission$.subscribe(async (mission: Mission) => {
      await this.init(mission);

      // Get current mission skids & analyzer
      if (this.skidsSubscription) { this.skidsSubscription.unsubscribe(); }
      this.skidsSubscription = this.skidsService.missionSkids$.subscribe(async (missionSkids: SkidMission[]) => {
        if (missionSkids != null) {
          this.missionSkids = missionSkids;

          // Set equipments composing the skids and mission skids
          this.materialsService.setSkidsEquipments(this.skidsService.skids$.getValue());
          this.materialsService.setMissionSkidsEquipments(this.missionSkids);
        } else {
          await this.backToMissionConfig();
        }
      });

      if(this.analyzerSubscription) { this.analyzerSubscription.unsubscribe(); }
      this.analyzerSubscription = this.analyzersServices.missionAnalyzers$.subscribe(async (missionAnalyzers: AnalyzerMission[]) => {
        this.missionAnalyzers = missionAnalyzers ?? [];
        this.materialsService.setMissionAnalyzersEquipments(this.missionAnalyzers);
      });
    });
  }

  ngOnDestroy(): void {
    if (this.missionSubscription) { this.missionSubscription.unsubscribe(); }
    if (this.skidsSubscription) { this.skidsSubscription.unsubscribe(); }
    if (this.analyzerSubscription) { this.analyzerSubscription.unsubscribe(); }
  }

  async init(mission: Mission): Promise<void> {
    this.mission = mission;

    this.loader.show();

    // Set global skids, navigate back to mission configuration if couldn't be fetched
    const skids = await this.skidsService.getSkids(mission.marketname);
    const pumps = await this.pumpsService.getPumps(mission.marketname, mission.missionId);
    const gateways = await this.gatewaysService.getGateways(mission.marketname, mission.missionId);
    const flowmeters = await this.flowmetersService.getFlowmeters(mission.marketname, mission.missionId);
    const analyzers = await this.analyzersServices.getAnalyzers(mission.marketname, mission.missionId);
    const trains = await this.trainsService.getMissionTrains(mission.missionId);
    const injections = await this.injectionsService.getMissionInjections(mission.missionId);

    this.loader.hide();

    if (skids == null || pumps == null || gateways == null || flowmeters == null || analyzers == null || trains == null || injections == null) {
      await this.backToMissionConfig(); 
    } else {
      this.missionTrains = trains;
      this.missionInjections = injections;
      this.skidsService.skids$.next(skids);
      this.pumpsService.pumps$.next(pumps);
      this.gatewaysService.gateways$.next(gateways);
      this.flowmetersService.flowmeters$.next(flowmeters);
      this.analyzersServices.analyzers$.next(analyzers);
    }
  }

  async backToMissionConfig() {
    await this.navigationService.navigateFromHome(CONSTANTS.ROUTES.MISSION_CONFIG);
  }

  openAddSkidModal(missionSkid?: SkidMission): void {
    this.missionSkidToUpdate = !!missionSkid ? missionSkid : null;
    this.addingSkid = true;
  }

  async closeAddSkidModal(selection?: SkidSelection): Promise<void> {
    this.addingSkid = false;
    if (!selection) { return; }

    const {skid, trainpk} = selection;
    const targetInjections = this.missionInjections.filter(injection => injection.trainpk === trainpk);
    const assignments = targetInjections.length ? targetInjections : [null];
    let allUpdated = true;

    for (const injection of assignments) {
      const missionSkid = new SkidMission({
        skidId: skid.skidId,
        pumpId: skid.pumpId,
        gatewayId: skid.gatewayId,
        flowmeterId: skid.flowmeterId,
        fieldprotocolName: skid.fieldprotocolName,
        trainpk,
        trainId: this.missionTrains.find(train => train.trainpk === trainpk)?.trainId,
        injectionpk: injection?.injectionpk,
        productName: injection?.productName,
        changedBy: null,
        changedOn: Math.floor(Date.now() / 1000).toString(),
        createdBy: null,
        createdOn: null
      });
      allUpdated = await this.skidsService.addUpdateMissionSkid(
        this.mission.missionId, missionSkid, injection?.injectionpk, trainpk
      ) && allUpdated;
    }

    if (allUpdated) {
      const missionSkids = await this.skidsService.getMissionSkids(this.mission.missionId);
      if (missionSkids != null) { this.skidsService.missionSkids$.next(missionSkids); }
      if (this.mission.materialstatus !== CONSTANTS.MISSION.CONFIG_STATUS.IN_PROGRESS) {
        await this.setMaterialStatus(CONSTANTS.MISSION.CONFIG_STATUS.IN_PROGRESS);
      }
    }
  }

  async removeMissionSkid(missionSkid: SkidMission): Promise<void> {
    const isMissionSkidRemoved = await this.skidsService.removeMissionSkid(this.mission.missionId, missionSkid.skidId);
    if (isMissionSkidRemoved) {
      const missionSkids = this.skidsService.missionSkids$.getValue().filter(skid => skid.skidId !== missionSkid.skidId);
      this.skidsService.missionSkids$.next(missionSkids);
    }
  }

  openAnalyzerModal(): void {
    this.addingAnalyzer = true;
  }

  async closeAnalyzerModal(selection?: AnalyzerSelection): Promise<void> {
    this.addingAnalyzer = false;
    if (!selection?.analyzer || selection.trainpk == null) { return; }
    const analyzer = selection.analyzer;

    const missionAnalyzer = new AnalyzerMission({
        analyzerpk: analyzer.analyzerpk, 
        analyzerId: analyzer.analyzerId,
        trainpk: selection.trainpk,
        trainId: this.missionTrains.find(train => train.trainpk === selection.trainpk)?.trainId,
        gatewayId: analyzer.telemetrysuppliername === CONSTANTS.TELEMETRY_SUPPLIER.ARKEMA ? analyzer.gatewayId : null,
        fieldprotocolName: analyzer.fieldprotocolName,
        handleFlyingH2: selection.handleFlyingH2 === true,
        changedBy: null,
        changedOn: Math.floor(Date.now() / 1000).toString(),
        createdBy: null,
        createdOn: null
    });

    this.materialsService.setMissionAnalyzersEquipments([missionAnalyzer]);

    const isAnalyzerUpdated = await this.analyzersServices.addUpdateMissionAnalyzer(this.mission.missionId, missionAnalyzer);
    if (isAnalyzerUpdated) {
      const missionAnalyzers = await this.analyzersServices.getMissionAnalyzers(this.mission.missionId);
      if (missionAnalyzers != null) { this.analyzersServices.missionAnalyzers$.next(missionAnalyzers); }
    }

    if (this.mission.materialstatus !== CONSTANTS.MISSION.CONFIG_STATUS.IN_PROGRESS) {
      // Set material status to 'IN PROGRESS'
      await this.setMaterialStatus(CONSTANTS.MISSION.CONFIG_STATUS.IN_PROGRESS);
    }
  }

  async removeAnalyzer(missionAnalyzer: AnalyzerMission): Promise<void> {
    const isAnalyzerRemoved = await this.analyzersServices.removeMissionAnalyzer(
      this.mission.missionId, missionAnalyzer.analyzerId, missionAnalyzer.trainpk
    );
    if (isAnalyzerRemoved) {
      const missionAnalyzers = (this.analyzersServices.missionAnalyzers$.getValue() ?? [])
        .filter(analyzer => !(analyzer.analyzerId === missionAnalyzer.analyzerId && analyzer.trainpk === missionAnalyzer.trainpk));
      this.analyzersServices.missionAnalyzers$.next(missionAnalyzers);
    }
  }

  async setMaterialStatus(status: number): Promise<void> {
    // Update material status
    const isStatusUpdated = await this.missionsService.updateMissionMaterialStatus(this.mission.missionId, status);

    // If update succeeded, set mission material status
    if (isStatusUpdated) {
      this.mission.materialstatus = status;
    }
  }

  isMaterialComplete(): boolean {
    return this.mission.materialstatus >= CONSTANTS.MISSION.CONFIG_STATUS.COMPLETED;
  }

  get uniqueMissionSkids(): SkidMission[] {
    return [...new Map(this.missionSkids.map(missionSkid => [missionSkid.skidId, missionSkid])).values()];
  }

  skidProductsLabel(skidId: string): string {
    return [...new Set(this.missionSkids
      .filter(missionSkid => missionSkid.skidId === skidId)
      .map(missionSkid => missionSkid.productName)
      .filter(Boolean))].join(', ');
  }


  async toggleMaterialStatus(): Promise<void> {
    const status = (this.mission.materialstatus >= CONSTANTS.MISSION.CONFIG_STATUS.COMPLETED) ?
                    CONSTANTS.MISSION.CONFIG_STATUS.IN_PROGRESS :
                    CONSTANTS.MISSION.CONFIG_STATUS.COMPLETED;

    

    // Validate material for 'COMPLETED' status
    if (status === CONSTANTS.MISSION.CONFIG_STATUS.COMPLETED ) {
      this.validatingMaterial = true;
    } else {
      await this.setMaterialStatus(status);
    }
  }

  closeMaterialValidation(): void {
    this.validatingMaterial = false;
  }
}
